import { useEffect, useState, useCallback } from 'react';
import { get } from 'lodash';
import {
  Box, Card, Container, Stack, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, CircularProgress,
  Alert, Breadcrumbs, Link, Select, MenuItem, FormControl, InputLabel,
  Pagination, Tooltip,
} from '@mui/material';
import { Icon } from '@iconify/react';
import Page from '../../components/Page';
import { getAppLogs, getAppLogDetail, getAppLogStats } from '../../axiosLib/services/appLogs/admin';

// ── Helpers ──

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  }) + '  ' + d.toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const levelConfig = {
  error: { color: '#ef4444', bg: '#fef2f2', icon: 'mdi:alert-circle', label: 'ERROR' },
  warn:  { color: '#f59e0b', bg: '#fffbeb', icon: 'mdi:alert',        label: 'WARN' },
};

const sourceConfig = {
  'node-backend': { color: '#22c55e', bg: '#f0fdf4', icon: 'mdi:nodejs',  label: 'Node Backend' },
  'omr-desktop':  { color: '#8b5cf6', bg: '#faf5ff', icon: 'mdi:desktop-classic', label: 'OMR Desktop' },
  'unknown':      { color: '#94a3b8', bg: '#f8fafc', icon: 'mdi:help-circle',     label: 'Unknown' },
};

// ── Stats Cards ──

const StatCard = ({ icon, label, value, color }) => (
  <Card sx={{
    p: 2.5, borderRadius: 2.5, border: '1px solid #f1f5f9',
    transition: 'box-shadow 0.2s', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.04)' },
    position: 'relative', overflow: 'hidden',
  }}>
    <Box sx={{
      position: 'absolute', top: 0, left: 0, right: 0, height: 3,
      background: `linear-gradient(90deg, ${color}, ${color}88)`,
    }} />
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box sx={{
        width: 42, height: 42, borderRadius: 2, display: 'flex',
        alignItems: 'center', justifyContent: 'center', bgcolor: `${color}12`,
      }}>
        <Icon icon={icon} style={{ fontSize: 22, color }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: '#1e1b4b', lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  </Card>
);

// ── Detail View ──

const LogDetailView = ({ log, onBack }) => {
  const lc = levelConfig[log.level] || levelConfig.error;
  const sc = sourceConfig[log.source] || sourceConfig.unknown;

  return (
    <Page title="App Log Detail">
      <Container maxWidth="xl" sx={{ pt: 3, pb: 5 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link underline="hover" color="inherit" sx={{ cursor: 'pointer', fontSize: '0.85rem' }} onClick={onBack}>
            App Logs
          </Link>
          <Typography color="text.primary" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Detail</Typography>
        </Breadcrumbs>

        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <IconButton onClick={onBack} sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
            <Icon icon="mdi:arrow-left" style={{ fontSize: 20 }} />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e1b4b' }}>Log Detail</Typography>
              <Chip label={lc.label} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22, bgcolor: lc.bg, color: lc.color }} />
              <Chip icon={<Icon icon={sc.icon} style={{ fontSize: 14 }} />} label={sc.label} size="small"
                sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22, bgcolor: sc.bg, color: sc.color }} />
            </Stack>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontFamily: 'monospace' }}>
              {log._id}
            </Typography>
          </Box>
        </Stack>

        {/* Message */}
        <Card sx={{ p: 2.5, mb: 2, borderRadius: 2.5, borderLeft: `4px solid ${lc.color}` }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
            Message
          </Typography>
          <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e1b4b', wordBreak: 'break-word' }}>
            {log.message}
          </Typography>
        </Card>

        {/* Location Markers */}
        {(log.module || log.function || log.file) && (
          <Card sx={{ p: 2.5, mb: 2, borderRadius: 2.5, border: '1px solid #f1f5f9' }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
              Location
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1.5 }}>
              {log.errorType && <InfoRow icon="mdi:bug" label="Error Type" value={log.errorType} />}
              {log.module && <InfoRow icon="mdi:puzzle" label="Module" value={log.module} />}
              {log.function && <InfoRow icon="mdi:function-variant" label="Function" value={log.function} />}
              {log.file && <InfoRow icon="mdi:file-code" label="File" value={log.file} />}
              {!!log.line && <InfoRow icon="mdi:format-list-numbered" label="Line" value={log.line} />}
            </Box>
          </Card>
        )}

        {/* Request Context (Node backend) */}
        {(log.method || log.url) && (
          <Card sx={{ p: 2.5, mb: 2, borderRadius: 2.5, border: '1px solid #f1f5f9' }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
              Request Context
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1.5 }}>
              {log.method && <InfoRow icon="mdi:swap-horizontal" label="Method" value={log.method} />}
              {log.url && <InfoRow icon="mdi:link" label="URL" value={log.url} />}
              {log.ip && <InfoRow icon="mdi:ip-network" label="IP" value={log.ip} />}
              {!!log.statusCode && <InfoRow icon="mdi:numeric" label="Status" value={log.statusCode} />}
              {log.userAgent && <InfoRow icon="mdi:web" label="User Agent" value={log.userAgent} />}
            </Box>
          </Card>
        )}

        {/* User & Environment */}
        <Card sx={{ p: 2.5, mb: 2, borderRadius: 2.5, border: '1px solid #f1f5f9' }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
            Context
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1.5 }}>
            {log.userId && <InfoRow icon="mdi:account" label="User ID" value={log.userId} />}
            {log.username && <InfoRow icon="mdi:at" label="Username" value={log.username} />}
            {log.institutionId && <InfoRow icon="mdi:domain" label="Institution" value={log.institutionId} />}
            {log.hostname && <InfoRow icon="mdi:server" label="Host" value={log.hostname} />}
            {log.platform && <InfoRow icon="mdi:monitor" label="Platform" value={log.platform} />}
            {log.nodeVersion && <InfoRow icon="mdi:nodejs" label="Node" value={log.nodeVersion} />}
            {log.pythonVersion && <InfoRow icon="mdi:language-python" label="Python" value={log.pythonVersion} />}
            <InfoRow icon="mdi:clock" label="Time" value={formatDate(log.createdAt)} />
          </Box>
        </Card>

        {/* Stack Trace */}
        {log.stack && (
          <Card sx={{ p: 2.5, mb: 2, borderRadius: 2.5, border: '1px solid #f1f5f9' }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
              Stack Trace
            </Typography>
            <Box sx={{
              p: 2, borderRadius: 2, bgcolor: '#1e1b4b', overflow: 'auto', maxHeight: 400,
            }}>
              <pre style={{ color: '#e2e8f0', fontSize: 12, margin: 0, whiteSpace: 'pre-wrap', fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}>
                {log.stack}
              </pre>
            </Box>
          </Card>
        )}

        {/* Meta */}
        {log.meta && Object.keys(log.meta).length > 0 && (
          <Card sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #f1f5f9' }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
              Extra Metadata
            </Typography>
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', overflow: 'auto', maxHeight: 300 }}>
              <pre style={{ color: '#334155', fontSize: 12, margin: 0, whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(log.meta, null, 2)}
              </pre>
            </Box>
          </Card>
        )}
      </Container>
    </Page>
  );
};

const InfoRow = ({ icon, label, value }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
    <Icon icon={icon} style={{ fontSize: 16, color: '#94a3b8', flexShrink: 0 }} />
    <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, flexShrink: 0 }}>{label}:</Typography>
    <Typography sx={{
      fontSize: '0.82rem', color: '#1e1b4b', fontWeight: 500, fontFamily: 'monospace',
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }}>
      {value}
    </Typography>
  </Stack>
);

// ── Main Component ──

export default function AppLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [stats, setStats] = useState(null);

  // Filters
  const [sourceFilter, setSourceFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchLogs = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = { page, limit: 30 };
    if (sourceFilter) params.source = sourceFilter;
    if (levelFilter) params.level = levelFilter;

    getAppLogs(params, (err, data) => {
      setLoading(false);
      if (err) {
        setError(typeof err === 'string' ? err : 'Unable to load app logs.');
        return;
      }
      setLogs(get(data, 'logs', []));
      setTotalPages(get(data, 'pages', 1));
      setTotal(get(data, 'total', 0));
    });
  }, [page, sourceFilter, levelFilter]);

  const fetchStats = useCallback(() => {
    getAppLogStats((err, data) => {
      if (!err) setStats(get(data, 'stats', null));
    });
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleViewDetail = (logId) => {
    setDetailLoading(true);
    getAppLogDetail(logId, (err, data) => {
      setDetailLoading(false);
      if (err) {
        setError('Failed to load log details');
        return;
      }
      setSelectedLog(get(data, 'log', null));
    });
  };

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  // ── Detail ──
  if (selectedLog) {
    return <LogDetailView log={selectedLog} onBack={() => setSelectedLog(null)} />;
  }

  // ── List ──
  return (
    <Page title="App Logs">
      <Container maxWidth="xl" sx={{ pt: 3, pb: 5 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e1b4b' }}>
              App Logs
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8', mt: 0.5 }}>
              Errors and warnings from Node.js backend & OMR desktop app
            </Typography>
          </Box>
          <IconButton onClick={() => { fetchLogs(); fetchStats(); }} sx={{
            bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' },
          }}>
            <Icon icon="mdi:refresh" style={{ fontSize: 22 }} />
          </IconButton>
        </Stack>

        {/* Stats */}
        {stats && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 2, mb: 3 }}>
            <StatCard icon="mdi:alert-circle" label="Total Errors" value={stats.totalErrors} color="#ef4444" />
            <StatCard icon="mdi:alert" label="Total Warnings" value={stats.totalWarns} color="#f59e0b" />
            <StatCard icon="mdi:nodejs" label="Node Errors" value={stats.nodeErrors} color="#22c55e" />
            <StatCard icon="mdi:desktop-classic" label="OMR Errors" value={stats.omrErrors} color="#8b5cf6" />
            <StatCard icon="mdi:clock-alert" label="Last 24h" value={stats.last24h} color="#0ea5e9" />
          </Box>
        )}

        {/* Filters */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Source</InputLabel>
            <Select value={sourceFilter} label="Source" onChange={handleFilterChange(setSourceFilter)}
              sx={{ borderRadius: 2, bgcolor: '#fff' }}>
              <MenuItem value="">All Sources</MenuItem>
              <MenuItem value="node-backend">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Icon icon="mdi:nodejs" style={{ fontSize: 16, color: '#22c55e' }} />
                  <span>Node Backend</span>
                </Stack>
              </MenuItem>
              <MenuItem value="omr-desktop">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Icon icon="mdi:desktop-classic" style={{ fontSize: 16, color: '#8b5cf6' }} />
                  <span>OMR Desktop</span>
                </Stack>
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Level</InputLabel>
            <Select value={levelFilter} label="Level" onChange={handleFilterChange(setLevelFilter)}
              sx={{ borderRadius: 2, bgcolor: '#fff' }}>
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="error">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Icon icon="mdi:alert-circle" style={{ fontSize: 16, color: '#ef4444' }} />
                  <span>Error</span>
                </Stack>
              </MenuItem>
              <MenuItem value="warn">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Icon icon="mdi:alert" style={{ fontSize: 16, color: '#f59e0b' }} />
                  <span>Warning</span>
                </Stack>
              </MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8', alignSelf: 'center' }}>
            {total} log{total !== 1 ? 's' : ''} found
          </Typography>
        </Stack>

        {/* Error */}
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {/* Loading */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#6366f1' }} />
          </Box>
        ) : logs.length === 0 ? (
          <Card sx={{ p: 6, borderRadius: 3, textAlign: 'center', border: '1px solid #e2e8f0' }}>
            <Icon icon="mdi:check-circle-outline" style={{ fontSize: 48, color: '#22c55e' }} />
            <Typography sx={{ mt: 2, fontWeight: 600, color: '#94a3b8' }}>No logs found — all clear!</Typography>
          </Card>
        ) : (
          <>
            <Card sx={{ borderRadius: 3, border: '1px solid rgba(99,102,241,0.08)', overflow: 'hidden' }}>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': {
                      bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.75rem', color: '#475569',
                      borderBottom: '2px solid #e2e8f0',
                    } }}>
                      <TableCell width={30}>#</TableCell>
                      <TableCell width={70}>Level</TableCell>
                      <TableCell width={120}>Source</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell width={140}>Location</TableCell>
                      <TableCell width={155}>Time</TableCell>
                      <TableCell width={40} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.map((log, idx) => {
                      const lc = levelConfig[log.level] || levelConfig.error;
                      const sc = sourceConfig[log.source] || sourceConfig.unknown;
                      const locationStr = [log.module, log.function].filter(Boolean).join('::') || log.file || '—';
                      return (
                        <TableRow
                          key={log._id}
                          hover
                          onClick={() => handleViewDetail(log._id)}
                          sx={{
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            '&:hover': { bgcolor: '#f0f9ff' },
                            '& td': { py: 1.3 },
                            borderLeft: `3px solid ${lc.color}`,
                          }}
                        >
                          <TableCell sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                            {(page - 1) * 30 + idx + 1}
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={<Icon icon={lc.icon} style={{ fontSize: 14 }} />}
                              label={lc.label}
                              size="small"
                              sx={{
                                fontWeight: 700, fontSize: '0.65rem', height: 22,
                                bgcolor: lc.bg, color: lc.color,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={<Icon icon={sc.icon} style={{ fontSize: 13 }} />}
                              label={sc.label}
                              size="small"
                              sx={{
                                fontWeight: 600, fontSize: '0.65rem', height: 22,
                                bgcolor: sc.bg, color: sc.color,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={log.message} placement="top-start" arrow>
                              <Typography sx={{
                                fontSize: '0.8rem', color: '#1e1b4b', fontWeight: 500,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                maxWidth: 400,
                              }}>
                                {log.message}
                              </Typography>
                            </Tooltip>
                            {log.errorType && (
                              <Typography sx={{ fontSize: '0.68rem', color: lc.color, fontFamily: 'monospace' }}>
                                {log.errorType}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography sx={{
                              fontSize: '0.72rem', color: '#64748b', fontFamily: 'monospace',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              maxWidth: 140,
                            }}>
                              {locationStr}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', color: '#475569' }}>
                            {formatDate(log.createdAt)}
                          </TableCell>
                          <TableCell>
                            {detailLoading ? (
                              <CircularProgress size={14} sx={{ color: '#6366f1' }} />
                            ) : (
                              <Icon icon="mdi:chevron-right" style={{ fontSize: 18, color: '#94a3b8' }} />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary"
                  shape="rounded"
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </Page>
  );
}
